(ns common.schema.constraints
  (:require
   [common.email :as email]
   [clojure.string :as string]
   [common.util :as cu]
   [common.number-utils :as num-util]))

(defn validate-finnish-business-id
  [_entity-data _entity-id value _unique-check _tr _logger]
  (let [y-tunnus value
        international-pattern #"^[Ff][Ii]\d\d\d\d\d\d\d\d$"
        pattern #"^\d\d\d\d\d\d\d-\d$"
        y-tunnus (when y-tunnus
                   (-> y-tunnus
                       str
                       string/trim))
        y-tunnus (if (re-find international-pattern y-tunnus)
                   (str (subs y-tunnus 2 9)
                        "-"
                        (subs y-tunnus 9 10))
                   y-tunnus)
        [nums-str checksum-str] (when (re-find pattern y-tunnus)
                                  (string/split y-tunnus #"-"))
        nums (when nums-str
               (->> nums-str
                    seq
                    (map str)
                    (map num-util/parse-int)))
        multipliers [7 9 10 5 8 4 2]
        sum (apply + (map * nums multipliers))
        reminder (rem sum 11)
        result (if (zero? reminder)
                 0
                 (- 11 reminder))
        checksum (when checksum-str
                   (num-util/parse-int checksum-str))]
    (when-not (and (number? result)
                   (= checksum result))
      "Not a valid Finnish business ID.")))

(def constraints
  [:constraint/mandatory-fn
   :constraint/unique-tenant-ignore-case-additional-attribute
   :constraint/unique-tenant-ignore-case
   :constraint/string-max-length
   :constraint/string-min-length
   :constraint/number-positive
   :constraint/number-negative
   :constraint/greater-than-inclusive
   :constraint/greater-than
   :constraint/smaller-than
   :constraint/smaller-than-inclusive
   :constraint/investor-or-person-company
   :constraint/valid-email
   :constraint/valid-string-value
   :constraint/valid-url])

(defn _validate-
  [[c-k c-v] entity-id value entity-data unique-check tr logger]
  (case c-k
    :constraint/mandatory-fn (when (c-v value entity-data)
                               (tr :constraint-violation/mandatory-must-contain-value))
    ;; TODO: how to implement - this can only be implemented within txn ;; do we add some pre-validation ?
    :constraint/unique-tenant-ignore-case
    (when-let [validation-fn (:validation-fn c-v)]
      (validation-fn entity-data entity-id value unique-check tr logger))
    :constraint/unique-tenant-ignore-case-additional-attribute
    (when-let [validation-fn (:validation-fn c-v)]
      (validation-fn entity-data entity-id value unique-check tr logger))

    ;; TODO: string constraints should trim ??
    :constraint/string-max-length (when (> (->> value
                                                (cu/str-trim)
                                                (count))
                                           c-v)
                                    (tr :constraint-violation/string-max-length [c-v]))
    :constraint/string-min-length (when (< (->> value
                                                (cu/str-trim)
                                                (count))
                                           c-v)
                                    (tr :constraint-violation/string-min-length [c-v]))
    :constraint/number-positive (when-not (>= value 0)
                                  (tr :constraint-violation/number-positive))
    :constraint/number-negative (when-not (< value 0)
                                  (tr :constraint-violation/number-negative))
    :constraint/greater-than-inclusive (when-not (>= value c-v)
                                         (tr :constraint-violation/greater-than-inclusive [c-v]))
    :constraint/greater-than (when-not (> value c-v)
                               (tr :constraint-violation/greater-than [c-v]))
    :constraint/smaller-than (when-not (< value c-v)
                               (tr :constraint-violation/smaller-than [c-v]))
    :constraint/smaller-than-inclusive (when-not (<= value c-v)
                                         (tr :constraint-violation/smaller-than-inclusive [c-v]))
    :constraint/valid-email (when-not (email/valid-email-pattern? value)
                              (tr :constraint-violation/email-pattern [value]))
    :constraint/investor-or-person-company
    (when-let [validation-fn (:validation-fn c-v)]
      (validation-fn entity-data entity-id value unique-check tr logger))
    :constraint/valid-string-value
    (when-let [validation-fn (:validation-fn c-v)]
      (validation-fn entity-data entity-id value unique-check tr logger))
    :constraint/valid-url nil ;; TODO: url validation regex
    nil                       ;; TODO: unknown constraint logging
    ))

(defn validate
  [constraints-map entity-id value entity-data unique-check tr & [logger]]
  (some->> constraints-map
           (keep
            (fn [constraint]
              (let [error
                    (_validate- constraint entity-id value entity-data unique-check tr logger)]

                (when (and logger error)
                  (logger (str "VALIDATE : " constraint " " value " -> " error)))
                error)))
           (first)))                                        ;; only first error returned

(defn validate-field
  [field-schema entity-id value entity-data unique-check tr & [logger]]
  (let [constraints (:field/constraints field-schema)
        validation-error-result
        (validate constraints entity-id value entity-data unique-check tr logger)]
    validation-error-result))

(defn tenant-unique-ignore-case
  [attribute entity-types _entity-data entity-id attribute-value unique-check _tr & [_logger]]
  (let [l-name (cu/str-trim-lcase attribute-value)]
    (unique-check entity-types attribute l-name entity-id)))

(defn investor-or-person-company
  [_attribute _entity-types entity-data _entity-id attribute-value _unique-check _tr & [_logger]]
  (when (and (= (:pipeline/entity-type entity-data)
                :entity.type/sales-opportunity)
             (seq attribute-value)
             (let [attribute-value (set attribute-value)]
               (not (or (= attribute-value #{:entity.type/investor})
                        (= attribute-value #{:entity.type/company})
                        (= attribute-value #{:entity.type/person})
                        (= attribute-value #{:entity.type/person :entity.type/company})))))
    "Investor not allowed together with contact/company"))

(defn tenant-unique-ignore-case-additional-attribute
  [attribute secondary-attribute entity-types entity-data entity-id attribute-value unique-check _tr
   & [_logger]]
  (let [l-name (cu/str-trim-lcase attribute-value)]
    (unique-check entity-types
                  attribute
                  l-name
                  entity-id
                  secondary-attribute
                  (get entity-data secondary-attribute))))

(defn validate-sso-login-path-string-pattern
  [value tr]
  (cond
    (not (string? value))
    (tr :invalid-data-type)

    (cu/blank-value? (string/trim value))
    (tr :required)

    (not (re-matches #"^[a-zA-Z0-9-]*" (str value)))
    (tr :custom-login-path-must-be-valid-path)))

(defn validate-sso-login-path-against-legacy-tenant-login-path
  [unique-check value]
  (unique-check value))

(defn validate-sso-login-path
  [_entity-data _entity-id value unique-check tr _logger]
  (or (validate-sso-login-path-string-pattern value tr)
      (validate-sso-login-path-against-legacy-tenant-login-path unique-check value)))
